// ─────────────────────────────────────────────────────────────────
// Glass-i — Product Creation Studio mode/nature helpers
// (Stage 6B.8A-3B).
//
// Plain-JS so node:test can import without a JSX loader. Drives the
// three creation modes (Quick / Guided / Advanced) and the six item
// natures (Glass / Profile / Hardware / Jobwork / Trading / Ecommerce)
// shipped on the entry screen.
//
// The point of this module is to keep the UX-layer rules (which
// fields are essential, which technical section auto-enables, which
// flags are pre-applied) in ONE testable place. The backend stays
// untouched.
// ─────────────────────────────────────────────────────────────────

// ── Creation modes ───────────────────────────────────────────────
export const CREATION_MODES = Object.freeze([
  {
    key:          'quick',
    label:        'Quick Create',
    tagline:      'Recommended',
    description:  'Fewest fields, fastest path. Best for ~80% of daily item creation.'
  },
  {
    key:          'guided',
    label:        'Guided Create',
    tagline:      '5-step flow',
    description:  'Structured but lean. Walks you through nature → details → GST → specs → review.'
  },
  {
    key:          'advanced',
    label:        'Advanced Create',
    tagline:      'Full control',
    description:  'Every core + extension field. For power users and complex hybrid items.'
  }
]);

export const CREATION_MODE_KEYS = Object.freeze(
  CREATION_MODES.map((m) => m.key)
);

// Default mode shown on the entry screen.
export const DEFAULT_CREATION_MODE = 'quick';

// ── Item natures ─────────────────────────────────────────────────
//
// `key`                — internal identifier
// `label`              — user-facing card title
// `description`        — copy on the card
// `extension`          — which 1:1 extension auto-enables in Quick/Guided
//                        ('glass' | 'profile' | 'hardware' | 'jobwork' |
//                         null for Trading; 'ecommerce' enables that
//                         extension specifically).
// `mini`               — array of field keys shown in the Quick mini-form
//                        (subset of the full extension; nothing else
//                        is rendered in Quick mode).
//
// These are the ONLY rules the UI uses to decide what to render in
// Quick + Guided modes. Advanced mode ignores `mini` and shows the
// full extension panel exactly as it did pre-3B.
export const ITEM_NATURES = Object.freeze([
  {
    key:          'glass',
    label:        'Glass Product',
    description:  'For toughened, laminated, DGU, cut-size, polished glass items.',
    extension:    'glass',
    mini:         ['thicknessMm', 'glassType', 'glassColor', 'processType', 'polishTypeId']
  },
  {
    key:          'profile',
    label:        'Profile / Section',
    description:  'For aluminium, UPVC, slim profile, frame, shutter, track, channel.',
    extension:    'profile',
    mini:         ['profileMaterial', 'profileSeries', 'profileColor', 'lengthMm', 'brand']
  },
  {
    key:          'hardware',
    label:        'Hardware / Accessory',
    description:  'For handles, hinges, locks, rollers, brackets, fittings, gaskets.',
    extension:    'hardware',
    mini:         ['hardwareType', 'hardwareCategory', 'hwBrand', 'hwModelNo', 'hwFinishType']
  },
  {
    key:          'jobwork',
    label:        'Jobwork / Service',
    description:  'For toughening, lamination, drilling, polishing, installation, transport, labour.',
    extension:    'jobwork',
    mini:         ['jobworkType', 'jobworkProcessType', 'chargeBasis', 'rateBasis', 'minimumCharge']
  },
  {
    key:          'trading',
    label:        'Trading Item',
    description:  'For simple buy-sell items without technical specs.',
    extension:    null,
    mini:         []
  },
  {
    key:          'ecommerce',
    label:        'Ecommerce Product',
    description:  'For products that should appear in catalog or enquiry screens.',
    extension:    'ecommerce',
    mini:         ['ecommerceTitle', 'ecommerceSlug', 'isVisibleOnline', 'publishStatus', 'visibility']
  }
]);

export const ITEM_NATURE_KEYS = Object.freeze(
  ITEM_NATURES.map((n) => n.key)
);

export function getNature(key) {
  if (!key) return null;
  return ITEM_NATURES.find((n) => n.key === key) || null;
}

// ── Smart defaults per nature ────────────────────────────────────
//
// Sets sensible flag defaults so the user doesn't have to remember
// what to tick for each item nature. The user can always override
// these — the banner makes the auto-applied values visible (never
// silent). Returned object overlays on top of the studio's
// DEFAULT_FORM_VALUES with `setFieldsValue`.
export function getSmartDefaultsForNature(key) {
  switch (key) {
    case 'glass':
      return {
        itemType:          'glass',
        isGoods:           true,
        isService:         false,
        isStockItem:       true,
        isSellable:        true,
        isPurchasable:     false,
        isManufacturable:  true,
        isJobworkItem:     false,
        isConsumable:      false,
        measurementMode:   'area_based',
        taxabilityType:    'taxable'
      };
    case 'profile':
      return {
        itemType:          'raw_material',
        isGoods:           true,
        isService:         false,
        isStockItem:       true,
        isSellable:        true,
        isPurchasable:     true,
        isManufacturable:  false,
        isJobworkItem:     false,
        isConsumable:      false,
        measurementMode:   'length_based',
        taxabilityType:    'taxable'
      };
    case 'hardware':
      return {
        itemType:          'trading_good',
        isGoods:           true,
        isService:         false,
        isStockItem:       true,
        isSellable:        true,
        isPurchasable:     true,
        isManufacturable:  false,
        isJobworkItem:     false,
        isConsumable:      false,
        measurementMode:   'qty_based',
        taxabilityType:    'taxable'
      };
    case 'jobwork':
      return {
        itemType:          'service',
        isGoods:           false,
        isService:         true,
        isStockItem:       false,
        isSellable:        true,
        isPurchasable:     false,
        isManufacturable:  false,
        isJobworkItem:     true,
        isConsumable:      false,
        measurementMode:   'service_based',
        taxabilityType:    'taxable'
      };
    case 'trading':
      return {
        itemType:          'trading_good',
        isGoods:           true,
        isService:         false,
        isStockItem:       true,
        isSellable:        true,
        isPurchasable:     true,
        isManufacturable:  false,
        isJobworkItem:     false,
        isConsumable:      false,
        measurementMode:   'qty_based',
        taxabilityType:    'taxable'
      };
    case 'ecommerce':
      // Defaults to trading-style flags PLUS hidden/draft publish state.
      return {
        itemType:          'trading_good',
        isGoods:           true,
        isService:         false,
        isStockItem:       true,
        isSellable:        true,
        isPurchasable:     true,
        isManufacturable:  false,
        isJobworkItem:     false,
        isConsumable:      false,
        measurementMode:   'qty_based',
        taxabilityType:    'taxable',
        publishStatus:     'draft',
        visibility:        'hidden',
        isVisibleOnline:   false
      };
    default:
      return {};
  }
}

// Which sections should auto-enable for a given nature. Used by the
// Quick + Guided save orchestration. Trading enables nothing.
export function getEnabledSectionsForNature(key) {
  const out = { glass: false, profile: false, hardware: false, jobwork: false, ecommerce: false };
  if (!key) return out;
  if (key === 'glass')    out.glass    = true;
  if (key === 'profile')  out.profile  = true;
  if (key === 'hardware') out.hardware = true;
  if (key === 'jobwork')  out.jobwork  = true;
  if (key === 'ecommerce') out.ecommerce = true;
  return out;
}

// Returns the list of mini-form field keys to render in Quick mode
// (and in Guided's "Item-specific details" step). Returns [] for
// natures with no extension (Trading).
export function getMiniFieldsForNature(key) {
  const nat = getNature(key);
  return nat && Array.isArray(nat.mini) ? nat.mini.slice() : [];
}

// Suggested non-flag friendly labels for the smart-defaults banner.
// Pure formatter so the banner component stays dumb. The banner only
// renders these — it does not interpret values.
export function describeSmartDefaults(defaults) {
  if (!defaults || typeof defaults !== 'object') return [];
  const out = [];
  const has = (k) => Object.prototype.hasOwnProperty.call(defaults, k);
  if (has('isGoods'))           out.push(`Goods: ${defaults.isGoods ? 'Yes' : 'No'}`);
  if (has('isService'))         out.push(`Service: ${defaults.isService ? 'Yes' : 'No'}`);
  if (has('isStockItem'))       out.push(`Stock: ${defaults.isStockItem ? 'Yes' : 'No'}`);
  if (has('isSellable'))        out.push(`Sellable: ${defaults.isSellable ? 'Yes' : 'No'}`);
  if (has('isPurchasable'))     out.push(`Purchasable: ${defaults.isPurchasable ? 'Yes' : 'No'}`);
  if (has('isManufacturable'))  out.push(`Manufacturable: ${defaults.isManufacturable ? 'Yes' : 'No'}`);
  if (has('isJobworkItem'))     out.push(`Jobwork: ${defaults.isJobworkItem ? 'Yes' : 'No'}`);
  if (has('measurementMode') && defaults.measurementMode) {
    const m = String(defaults.measurementMode).replace(/_based$/, '').replace(/_/g, ' ');
    out.push(`Measurement: ${m}`);
  }
  if (has('publishStatus') && defaults.publishStatus) {
    out.push(`Publish: ${defaults.publishStatus}`);
  }
  if (has('visibility') && defaults.visibility) {
    out.push(`Visibility: ${defaults.visibility}`);
  }
  return out;
}

// ── Guided-mode step definition (5 steps) ────────────────────────
//
// Mirrors STUDIO_STEPS in studioConstants.js but pares the 7 advanced
// steps down to 5 guided steps. The page renders the same step
// components, just in this shorter sequence + with conditional
// subsections.
export const GUIDED_STEPS = Object.freeze([
  { key: 'nature',        title: 'What are you creating?',  subtitle: 'Item nature + base stock' },
  { key: 'basic',         title: 'Basic Details',            subtitle: 'Name, code, category' },
  { key: 'gst_pricing',   title: 'GST, UOM & Rates',         subtitle: 'Tax, units, prices' },
  { key: 'technical',     title: 'Item-Specific Details',    subtitle: 'Only the relevant section' },
  { key: 'review',        title: 'Review & Save',            subtitle: 'Confirm and save' }
]);

export const GUIDED_STEP_KEYS = Object.freeze(
  GUIDED_STEPS.map((s) => s.key)
);

// ── Required-fields contract for Quick Create ───────────────────
//
// Frontend-only validation contract. Quick Create silently sends the
// rest of the core payload at defaults from `DEFAULT_FORM_VALUES`.
// The backend will still validate everything end-to-end.
export const QUICK_REQUIRED_FIELDS = Object.freeze([
  'itemName',
  'itemCode'
]);

// Helper consumed by the Quick-form submit button to grey itself out
// before any backend call. Returns the array of missing keys.
export function findMissingQuickFields(values) {
  if (!values || typeof values !== 'object') return QUICK_REQUIRED_FIELDS.slice();
  return QUICK_REQUIRED_FIELDS.filter((k) => {
    const v = values[k];
    return v === undefined || v === null || String(v).trim() === '';
  });
}

// ── Base-stock relationship — user-friendly enum ─────────────────
//
// The DB column is `source_stock_item_id` (UUID, nullable). The UI
// asks the user a yes/no question and reveals a picker on "Yes". We
// store a tri-state in component state:
//   'base'      → no source_stock_item_id
//   'derived'   → user must pick a source_stock_item_id
//   null        → not yet answered (entry screen flow)
export const BASE_STOCK_RELATIONSHIPS = Object.freeze([
  { value: 'base',    label: 'Base stock item',
    description: 'This item is its own raw / starting material.' },
  { value: 'derived', label: 'Finished or processed item',
    description: 'This item is made from another stock material (e.g. toughened glass from annealed glass).' }
]);

export function isDerivedRelationship(rel) {
  return rel === 'derived';
}
